import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Badge, Button, Card, CardBody, Col, Container, Input, Row, Spinner, Table
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import * as dayjs from "dayjs";
import * as XLSX from "xlsx";
import Session from "../../auth/Session";
import { Roles } from "../../auth/Roles";
import useSortableData from "../useSortableData";
import SortableHeader from "../SortableHeader";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");
const money = (n) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const days = (n) => {
    const fixed = Number(n ?? 0).toFixed(2);
    return fixed.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
};

// The exact WorldPac "DOS" submission header, matching the sample workbook (note the trailing space
// on "# of Drivers ").
const WORLDPAC_HEADER = [
    "Vendor #", "Invoice Date", "Invoice #", "Transaction #", "Transaction Date",
    "Branch Number", "Branch Name", "Vehicle Type (Pax Vehicle or Van)", "MOTC", "# of Drivers ",
    "Total Hours / MOTC", "Total MOTC Miles", "Additional Drive Time Hrs", "Additional Drive Time $",
    "Base Rate $", "Wear and Tear Cost $", "Fuel Cost (Surcharge Cost) $", "Taxes $", "Tolls/Other $", "Total $"
];

// Weekly invoice reconciliation: every sub-customer that ran routes this week alongside its invoice
// status and amount, so finance can confirm nothing that ran went un-invoiced and reconcile the total
// invoiced against QuickBooks. Also offers the WorldPac per-line ("DOS") export for a selected customer.
const InvoiceReconciliationReport = () => {
    const navigate = useNavigate();
    const [weeks, setWeeks] = useState([{ endingDate: "", displayDate: "" }]);
    const [selectedWeek, setSelectedWeek] = useState("");
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState("");
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(false);
    const [worldpacLoading, setWorldpacLoading] = useState(false);
    const [error, setError] = useState("");

    const sortGetters = useMemo(() => ({
        daysWorked: (r) => Number(r.daysWorked ?? 0),
        invoiceAmount: (r) => Number(r.invoiceAmount ?? 0),
    }), []);

    const { items: sortedRows, sortConfig, requestSort } = useSortableData(
        rows,
        { key: "subCustomerName", direction: "asc", getters: sortGetters }
    );

    useEffect(() => {
        if (!Session.hasRole(Roles.Admin)) {
            navigate("/accessdenied");
            return;
        }
        (async () => {
            const token = await Session.getToken();
            fetch("/api/routes/invoice-weeks", { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.ok ? r.json() : [])
                .then(list => {
                    setWeeks(list || []);
                    const preselect = (list || []).find(w => w.selected === true) || (list || [])[0];
                    if (preselect) setSelectedWeek(preselect.endingDate);
                })
                .catch(() => setWeeks([]));
            fetch("/api/Customers", { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.ok ? r.json() : [])
                .then(list => setCustomers((list || []).sort((a, b) => (a.name || "").localeCompare(b.name || ""))))
                .catch(() => setCustomers([]));
        })();
    }, [navigate]);

    useEffect(() => {
        if (!selectedWeek) return;
        (async () => {
            setLoading(true);
            setError("");
            try {
                const token = await Session.getToken();
                const endingDate = dayjs(selectedWeek, "MM/DD/YYYY").format("YYYY-MM-DD");
                const res = await fetch(`/api/reports/invoice-reconciliation?endingDate=${endingDate}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
                const data = await res.json();
                setRows(data.rows || []);
                setSummary(data.summary || null);
                setSchedule(data.schedule || null);
            } catch (e) {
                setError(e.message || "Failed to load invoice reconciliation report.");
                setRows([]);
                setSummary(null);
                setSchedule(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [selectedWeek]);

    // "Primary" download: the reconciliation grid itself.
    const exportToExcel = () => {
        const weekLabel = schedule ? `${fmtDate(schedule.startDate)} - ${fmtDate(schedule.endDate)}` : "";
        const header = ["Customer", "Sub-Customer", "Ran Routes", "Route Days", "Invoice Status", "Invoice Total", "QuickBooks", "Needs Attention"];
        const body = sortedRows.map(r => [
            r.customerName,
            r.subCustomerName,
            r.worked ? "Yes" : "No",
            r.worked ? Number(r.daysWorked ?? 0) : "",
            r.status,
            Number(r.invoiceAmount ?? 0),
            r.qbInvoiceId ? "Synced" : (r.hasInvoice && r.approved ? "Not sent" : ""),
            r.needsAttention ? "YES" : ""
        ]);
        const summaryRows = summary ? [
            [],
            ["Sub-customers with activity", summary.subCustomersWithActivity],
            ["Invoices", summary.invoices],
            ["Approved", summary.approvedCount],
            ["Ran but not invoiced", summary.missingCount],
            ["Total approved invoiced", Number(summary.totalApprovedAmount ?? 0)],
        ] : [];
        const sheetData = [
            ["Invoice Reconciliation", "", "", weekLabel],
            [],
            header,
            ...body,
            ...summaryRows,
        ];
        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Reconciliation");
        const fileWeek = schedule ? dayjs(schedule.startDate).format("YYYY-MM-DD") : "week";
        XLSX.writeFile(wb, `invoice_reconciliation_${fileWeek}.xlsx`);
    };

    // "Summary" download: the WorldPac per-line DOS format for the selected customer's approved invoices.
    const downloadWorldPac = async () => {
        if (!selectedCustomer || !selectedWeek) return;
        setWorldpacLoading(true);
        setError("");
        try {
            const token = await Session.getToken();
            const endingDate = dayjs(selectedWeek, "MM/DD/YYYY").format("YYYY-MM-DD");
            const res = await fetch(`/api/reports/worldpac-invoice-export?endingDate=${endingDate}&customerId=${selectedCustomer}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            const body = (data.rows || []).map(r => [
                r.vendorNumber ?? "",
                fmtDate(r.invoiceDate),
                r.invoiceNumber ?? "",
                "",
                fmtDate(r.transactionDate),
                r.branchNumber ?? "",
                r.branchName ?? "",
                r.vehicleType ?? "",
                r.motc ?? "",
                r.drivers ?? 1,
                "",
                "",
                Number(r.additionalDriveTimeHrs ?? 0),
                Number(r.additionalDriveTimeAmount ?? 0),
                Number(r.baseRate ?? 0),
                Number(r.wearAndTear ?? 0),
                Number(r.fuel ?? 0),
                Number(r.taxes ?? 0),
                Number(r.tollsOther ?? 0),
                Number(r.total ?? 0),
            ]);
            const ws = XLSX.utils.aoa_to_sheet([WORLDPAC_HEADER, ...body]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "NDS");
            const vendor = data.customer?.vendorNumber || data.customer?.name || "customer";
            const range = schedule ? `${dayjs(schedule.startDate).format("YYYY.MM.DD")}-${dayjs(schedule.endDate).format("YYYY.MM.DD")}` : "week";
            XLSX.writeFile(wb, `${vendor}_DOS_${range}.xlsx`);
        } catch (e) {
            setError(e.message || "Failed to build WorldPac export.");
        } finally {
            setWorldpacLoading(false);
        }
    };

    const statusBadge = (r) => {
        if (r.status === "Approved") return <Badge color="success">Approved</Badge>;
        if (r.status === "Submitted") return <Badge color="info">Submitted</Badge>;
        if (r.status === "In Review") return <Badge color="secondary">In Review</Badge>;
        if (r.status === "Voided") return <Badge color="warning">Voided</Badge>;
        return <Badge color="danger">No Invoice</Badge>;
    };

    return (
        <Container className="mt-0">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>Weekly Invoice Reconciliation</h5></Col>
                        <Col className="text-end">
                            <Button size="sm" color="success" className="me-2" onClick={exportToExcel} disabled={loading || rows.length === 0}>
                                Export to Excel
                            </Button>
                            <Button size="sm" color="primary" onClick={downloadWorldPac} disabled={worldpacLoading || !selectedCustomer || !selectedWeek}>
                                {worldpacLoading ? <><Spinner size="sm" /> Building...</> : "Download WorldPac Summary"}
                            </Button>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={5}>
                            <Input
                                type="select"
                                value={selectedWeek}
                                onChange={(e) => setSelectedWeek(e.target.value)}
                            >
                                <option value="">-- Select Week --</option>
                                {weeks.map(w => (
                                    <option key={w.endingDate} value={w.endingDate}>{w.displayDate}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={5}>
                            <Input
                                type="select"
                                value={selectedCustomer}
                                onChange={(e) => setSelectedCustomer(e.target.value)}
                                title="Customer for the WorldPac summary download"
                            >
                                <option value="">-- Customer (for WorldPac summary) --</option>
                                {customers.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </Input>
                        </Col>
                    </Row>

                    {error && <Alert color="danger">{error}</Alert>}

                    {summary && (
                        <Row className="mb-4">
                            <Col md={3}>
                                <Card className="shadow-sm"><CardBody>
                                    <div className="text-muted">Sub-Customers w/ Activity</div>
                                    <div className="h4 mb-0">{summary.subCustomersWithActivity}</div>
                                </CardBody></Card>
                            </Col>
                            <Col md={3}>
                                <Card className="shadow-sm"><CardBody>
                                    <div className="text-muted">Approved Invoices</div>
                                    <div className="h4 mb-0">{summary.approvedCount}</div>
                                </CardBody></Card>
                            </Col>
                            <Col md={3}>
                                <Card className={`shadow-sm ${summary.missingCount > 0 ? "border-danger" : ""}`}><CardBody>
                                    <div className="text-muted">Ran, Not Invoiced</div>
                                    <div className={`h4 mb-0 ${summary.missingCount > 0 ? "text-danger" : ""}`}>{summary.missingCount}</div>
                                </CardBody></Card>
                            </Col>
                            <Col md={3}>
                                <Card className="shadow-sm"><CardBody>
                                    <div className="text-muted">Total Approved Invoiced</div>
                                    <div className="h4 mb-0">${money(summary.totalApprovedAmount)}</div>
                                </CardBody></Card>
                            </Col>
                        </Row>
                    )}

                    {loading ? (
                        <div className="text-center py-4"><Spinner /></div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <SortableHeader label="Customer" sortKey="customerName" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Sub-Customer" sortKey="subCustomerName" sortConfig={sortConfig} onSort={requestSort} />
                                    <th>Ran Routes</th>
                                    <SortableHeader label="Route Days" sortKey="daysWorked" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <SortableHeader label="Invoice Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Invoice Total" sortKey="invoiceAmount" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <th>QuickBooks</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.length === 0 ? (
                                    <tr><td colSpan="8" className="text-center">No sub-customers or invoices for the selected week.</td></tr>
                                ) : sortedRows.map(r => (
                                    <tr key={r.subCustomerId} className={r.needsAttention ? "table-danger" : ""}>
                                        <td>{r.customerName}</td>
                                        <td>{r.subCustomerName}</td>
                                        <td className="text-center">{r.worked ? "Yes" : <span className="text-muted">No</span>}</td>
                                        <td style={{ textAlign: "right" }}>{r.worked ? days(r.daysWorked) : ""}</td>
                                        <td>{statusBadge(r)}</td>
                                        <td style={{ textAlign: "right" }}>{r.hasInvoice ? `$${money(r.invoiceAmount)}` : ""}</td>
                                        <td className="text-center">
                                            {r.qbInvoiceId
                                                ? <span className="text-success">Synced{r.qbDocNumber ? ` #${r.qbDocNumber}` : ""}</span>
                                                : r.hasInvoice && r.approved
                                                    ? <span className="text-muted">Not sent</span>
                                                    : <span className="text-muted">—</span>}
                                            {r.qbSyncError && <div className="text-danger small mt-1">{r.qbSyncError}</div>}
                                        </td>
                                        <td className="text-center">
                                            {r.needsAttention && <Badge color="danger">Not invoiced</Badge>}
                                            {r.hasInvoice && (
                                                <Button size="sm" color="secondary" outline className="ms-2" onClick={() => navigate(`/reports/invoice-view/${r.invoiceId}`)}>
                                                    View
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
        </Container>
    );
};

export default InvoiceReconciliationReport;
